import { useEffect, useState } from "react";
import Header from "./Header";
import Footer from "./Footer";
import ChatBot from "./ChatBot";
import "../styles/PracticeAreas.css";
import API_BASE_URL from "../config";
import {
  FaBalanceScale,
  FaChevronDown,
  FaChevronRight,
  FaShieldAlt,
  FaGavel,
  FaUsers,
  FaHome,
  FaFileContract,
  FaBriefcase,
  FaCar,
  FaUserInjured,
  FaRegHandshake,
  FaLandmark,
  FaGlobe,
  FaBook
} from "react-icons/fa";

// Icon mapping for different categories
const categoryIcons = {
  "Corporate Law": FaBriefcase,
  "Family Law": FaUsers,
  "Property Law": FaHome,
  "Employment Law": FaUserInjured,
  "Criminal Law": FaGavel,
  "Civil Law": FaBalanceScale,
  "Contract Law": FaFileContract,
  "Personal Injury": FaUserInjured,
  "Intellectual Property": FaBook,
  "Immigration Law": FaGlobe,
  "Tax Law": FaLandmark,
  "Insurance Law": FaShieldAlt,
  "Traffic Law": FaCar,
  "General Practice": FaBalanceScale,
  "Default": FaBalanceScale
};

export default function PracticeAreas() {
  const [brandName, setBrandName] = useState("");
  const [practiceData, setPracticeData] = useState({});
  const [openCategories, setOpenCategories] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [siteRes, practiceRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/site-settings`),
          fetch(`${API_BASE_URL}/api/practice-areas/public`)
        ]);

        const siteData = await siteRes.json();
        const practiceJson = await practiceRes.json();

        if (siteData?.brandName) setBrandName(siteData.brandName);
        if (practiceJson?.success) {
          setPracticeData(practiceJson.data);
          // Open first category by default
          const categories = Object.keys(practiceJson.data);
          if (categories.length > 0) {
            setOpenCategories({ [categories[0]]: true });
          }
        }
      } catch (err) {
        console.error("Failed to load practice areas", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const toggleCategory = category => {
    setOpenCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getCategoryIcon = (category) => {
    const Icon = categoryIcons[category] || categoryIcons.Default;
    return <Icon className="category-icon" />;
  };

  return (
    <div className="jus-practice-container">
      <Header />

      {/* HERO */}
      <section className="jus-practice-hero">
        <div className="jus-practice-hero-content">
          <div className="hero-badge-container">
            <span className="jus-practice-badge">
              <FaBalanceScale /> Legal Expertise
            </span>
          </div>
          <h1 className="jus-practice-title">
            Legal Practice <span>Areas</span>
          </h1>
          <p className="jus-practice-subtitle">
            Explore our comprehensive legal services organized by category. 
            Each area contains specialized legal solutions tailored to your needs.
          </p>
        </div>
        <div className="hero-decoration">
          <div className="decoration-line"></div>
          <div className="decoration-dot"></div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <main className="jus-practice-main">
        <div className="practice-areas-wrapper">
          <div className="practice-header">
            <h2>Browse Practice Areas</h2>
            <p className="practice-description">
              Select a category to view detailed legal services
            </p>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading practice areas...</p>
            </div>
          ) : Object.keys(practiceData).length === 0 ? (
            <div className="empty-state">
              <FaBalanceScale className="empty-icon" />
              <h3>No Practice Areas Available</h3>
              <p>Practice areas will be listed here once available.</p>
            </div>
          ) : (
            <div className="practice-tree-container">
              {/* TREE VIEW */}
              <div className="practice-tree">
                {Object.entries(practiceData).map(([category, areas]) => {
                  const isOpen = openCategories[category];
                  return (
                    <div 
                      key={category} 
                      className={`practice-category ${isOpen ? 'open' : ''}`}
                    >
                      {/* CATEGORY HEADER */}
                      <button
                        className="category-header"
                        onClick={() => toggleCategory(category)}
                        aria-expanded={isOpen}
                      >
                        <div className="category-header-left">
                          <div className="category-icon-wrapper">
                            {getCategoryIcon(category)}
                          </div>
                          <div className="category-info">
                            <h3 className="category-title">{category}</h3>
                            <span className="area-count">
                              {areas.length} {areas.length === 1 ? 'service' : 'services'}
                            </span>
                          </div>
                        </div>
                        <div className="category-toggle">
                          {isOpen ? (
                            <>
                              <span className="collapse-text">Collapse</span>
                              <FaChevronDown className="toggle-icon" />
                            </>
                          ) : (
                            <>
                              <span className="expand-text">Expand</span>
                              <FaChevronRight className="toggle-icon" />
                            </>
                          )}
                        </div>
                      </button>

                      {/* AREAS LIST */}
                      {isOpen && (
                        <div className="areas-list-container">
                          <div className="areas-list-divider"></div>
                          <ul className="areas-list">
                            {areas.map((area, index) => (
                              <li 
                                key={area} 
                                className="area-item"
                                style={{ animationDelay: `${index * 0.05}s` }}
                              >
                                <div className="area-item-content">
                                  <div className="area-bullet"></div>
                                  <div className="area-details">
                                    <h4 className="area-title">{area}</h4>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* SUMMARY CARD */}
              <div className="practice-summary">
                <div className="summary-header">
                  <FaShieldAlt className="summary-icon" />
                  <h3>Legal Expertise Summary</h3>
                </div>
                <div className="summary-stats">
                  <div className="stat-item">
                    <span className="stat-number">{Object.keys(practiceData).length}</span>
                    <span className="stat-label">Categories</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">
                      {Object.values(practiceData).reduce((sum, areas) => sum + areas.length, 0)}
                    </span>
                    <span className="stat-label">Total Services</span>
                  </div>
                </div>
                <div className="summary-content">
                  <p>
                    {brandName} connects you with specialized legal professionals 
                    across {Object.keys(practiceData).length} major practice areas. 
                    Each service is provided by licensed, independent solicitors 
                    with expertise in their respective fields.
                  </p>
                </div>
                <div className="summary-disclaimer">
                  <FaShieldAlt />
                  <p>
                    {brandName} facilitates connections with independent solicitors.
                    Legal advice and representation are provided solely by licensed
                    professionals.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <ChatBot />
      <Footer />
    </div>
  );
}